import { Component, signal, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { WebTableComponent } from '../../shared/web-table-component/web-table-component';
import { WebService } from '../../services/web-service';
import { WebIngestionModal } from './web-ingestion-modal/web-ingestion-modal';
import { WebIngestionDto } from '../../shared/interfaces';
import { ToastService } from '../../services/toast-service';
import { WebIngestionDuplicatesModal } from './web-ingestion-duplicates-modal/web-ingestion-duplicates-modal';
import { TaskService } from '../../services/task-service';

@Component({
  selector: 'app-web-sites',
  standalone: true,
  imports: [CommonModule, WebTableComponent],
  templateUrl: './web-sites.html',
  styleUrl: './web-sites.scss',
})
export class WebSites {
  public readonly webService = inject(WebService);
  private readonly taskService = inject(TaskService)
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService)
  
  websitesData = signal<any[]>([]);
  isLoading = signal<boolean>(true);
  
  // Traccia il nome dell'indice corrente per abilitare/disabilitare il pulsante
  activeIndexName = signal<string | null>(null);

  isProcessing = this.taskService.isProcessing;
  private readonly globalTaskState = this.taskService.taskState;

  constructor() {
    this.loadData();

    effect(() => {
      if (this.globalTaskState()?.status === 'COMPLETED') {
        this.loadData();
      }
    });
  }

  loadData() {
    this.isLoading.set(true);
    this.webService.getWebsites().subscribe({
      next: (data) => {
        this.websitesData.set(data);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Errore nel recupero dei siti web:', err);
        this.isLoading.set(false);
      }
    });
  }

  handleIndexChanged(indexName: string | null) {
    this.activeIndexName.set(indexName);
  }

  openWebIngestionModal() {
    const currentIndex = this.activeIndexName();
    if (!currentIndex) return;

    const modalRef = this.modalService.open(WebIngestionModal, { 
      centered: true, 
      windowClass: 'agentic-custom-modal' 
    });

    modalRef.componentInstance.indexName = currentIndex;

    modalRef.result.then((urlToCrawl: string) => {
      if (urlToCrawl) {
        // Prepariamo il payload iniziale
        const payload: WebIngestionDto = {
          urls: [urlToCrawl],
          indexName: currentIndex,
          type: 'auto' as const
        };
        this.executeIngestion(payload);
      }
    }).catch(() => {});
  }

  // --- FASE 2: ESECUZIONE CHIAMATA ---
  private executeIngestion(payload: WebIngestionDto) {
    this.isLoading.set(true);

    this.webService.crawlWebsite(payload).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        
        // GESTIONE DUPLICATI (Conflitti di URL)
        if (res.status === 'DUPLICATES_FOUND' && res.conflicts) {
          this.handleDuplicatesModal(payload, res);
        } 
        // GESTIONE REGOLARE
        else if (res.status === 'STARTED' && res.taskId) {
          this.taskService.registerNewTask(res.taskId);
          this.toastService.show('Scansione web avviata con successo', 'success');
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toastService.show('Errore durante l\'avvio della scansione', 'error');
      }
    });
  }

  // --- FASE 3: GESTIONE MODALE CONFLITTI ---
  private handleDuplicatesModal(originalPayload: WebIngestionDto, res: any) {
    const modalRef = this.modalService.open(WebIngestionDuplicatesModal, { 
      centered: true, 
      windowClass: 'agentic-custom-modal' 
    });

    modalRef.componentInstance.duplicatesList = res.conflicts;
    modalRef.componentInstance.conflictType = res.conflictType; 
    modalRef.componentInstance.allDuplicates = true; // Lato web inseriamo un URL alla volta

    modalRef.result.then((userChoice: 'replace' | 'cancel') => {
      if (!userChoice || userChoice === 'cancel') return; 

      if (userChoice === 'replace') {
        this.executeIngestion({ ...originalPayload, action: 'replace' });
      }
    }).catch(() => {
      // L'utente ha cliccato fuori o sulla X
    });
  }
}